// URL state for finance pages: tab, filters, the selected listino and which modal is open
// (DD-042, DD-045). A modal hands over to another by replacing `modal`, so modals never stack.
//
// History (S-F14): a modal opened from its list is a push. `location.state.modalDepth` counts the entries the modals
// added on top of the list and `modalBase` is the list's query, so closing goes back over them, and Back leaves the
// list in one step and never reopens a closed modal. It goes back only when the list it lands on is the one a replace
// would show: a hand-over that picked or created a listino closes by replace instead, so the selection is kept. A
// hand-over replace adds no entry. A modal reached by a deep link has no depth and closes by replace.
import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export type ParamPatch = Record<string, string | null>;

type LocationState = Record<string, unknown> | null;
const asState = (state: unknown): LocationState => (typeof state === "object" && state !== null ? (state as Record<string, unknown>) : null);

/** The query in one order, so two URLs with the same params compare equal. */
export const canonicalQuery = (params: URLSearchParams): string => {
  const sorted = new URLSearchParams(Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b)));
  return sorted.toString();
};

/** How many history entries the open modals sit on top of the list. */
export const modalDepth = (state: unknown): number => {
  const depth = asState(state)?.modalDepth;
  return typeof depth === "number" && depth > 0 ? depth : 0;
};

const modalBase = (state: unknown): string | null => {
  const base = asState(state)?.modalBase;
  return typeof base === "string" ? base : null;
};

/**
 * The location state a navigation carries: the breadcrumb trail and the rest stay. A push that opens a modal records
 * the list's query (`from`) and a depth of 1, a push made while one is open adds 1 (none for a deep-linked modal,
 * which has no list entry to go back to), a replace keeps both, and a navigation that ends with no modal drops them.
 */
export function modalState(state: unknown, nav: { hadModal: boolean; hasModal: boolean; replace: boolean; from: string }): LocationState {
  const rest = Object.fromEntries(Object.entries(asState(state) ?? {}).filter(([key]) => key !== "modalDepth" && key !== "modalBase"));
  const kept = { modalDepth: modalDepth(state), modalBase: modalBase(state) };
  let next: { modalDepth: number; modalBase: string | null } | null = null;
  if (nav.hasModal) {
    if (nav.replace) next = kept.modalDepth > 0 ? kept : null;
    else if (!nav.hadModal) next = { modalDepth: 1, modalBase: nav.from };
    else next = kept.modalDepth > 0 ? { ...kept, modalDepth: kept.modalDepth + 1 } : null;
  }
  if (next) return { ...rest, ...next };
  return state === null || state === undefined ? null : rest;
}

/** How many entries Close goes back: all the modals added when the list under them is what `after` shows, else 0 (replace). */
export function closeSteps(state: unknown, after: URLSearchParams): number {
  const depth = modalDepth(state);
  return depth > 0 && modalBase(state) === canonicalQuery(after) ? depth : 0;
}

/** A modal is open when the URL names one (`modal`) or a price list line (`line`, whose modal has no `modal`). */
const isModalOpen = (params: URLSearchParams): boolean => params.has("modal") || params.has("line");

/** Call `set` once per handler: two calls in one handler lose the first. */
export function useFinanceParams() {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const set = useCallback(
    (patch: ParamPatch, replace = false) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([key, value]) => (value === null ? next.delete(key) : next.set(key, value)));
      setParams(next, { replace, state: modalState(state, { hadModal: isModalOpen(params), hasModal: isModalOpen(next), replace, from: canonicalQuery(params) }) });
    },
    [params, setParams, state],
  );
  /** Closes the open modals: back over the entries they added, or a replace when going back would lose the selection. */
  const closeModals = useCallback(
    (patch: ParamPatch) => {
      const after = new URLSearchParams(params);
      Object.entries(patch).forEach(([key, value]) => (value === null ? after.delete(key) : after.set(key, value)));
      const steps = closeSteps(state, after);
      if (steps > 0) navigate(-steps);
      else set(patch, true);
    },
    [navigate, params, set, state],
  );
  return { params, set, closeModals };
}
